/**
 * PinnedCard — one pinned matter on the Operations Surface's Pinned subtab
 * (#541 Slice 2; upgrades Slice 1's light row).
 *
 * A pin is the operator's PRIVATE watch — "keep an eye on this for a reason no
 * rule captures." The card is status-shaped: it carries the matter's current
 * book-card detail (phase, firms, lifecycle signals) so a glance answers
 * "what's the situation now?" without opening the panel. Facts stay neutral
 * (colour = a call to action, ADR-0134); the phase stripe is the existing
 * neutral accent.
 *
 * The awareness → judgment lane (#541):
 *   - **Notes** — the pin IS a Task, so notes ride its existing message
 *     thread (`useTaskMessages` / `useAddMessage`; no new endpoint).
 *   - **+ To-do** — opens the Dashboard's create-task modal pre-bound to this
 *     matter (a locked lead `about`; same POST route as everywhere else).
 *
 * Clicking the name opens the shared case-detail panel (the parent owns that
 * state); Un-pin reuses the task `clear` action.
 */

import { useState } from "react";
import type { SurfaceCard, SurfacePin } from "@command-center/shared";
import { CreateTaskModal } from "@/routes/dashboard/create-task-modal";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { phaseForCardKind } from "@/lib/operations-phases";
import { operatorName } from "@/lib/operators";
import {
  useAddMessage,
  useTaskAction,
  useTaskMessages,
} from "@/lib/use-surface-tasks";
import {
  underReviewLeadToDetail,
  underReviewMiscToDetail,
  acceptedToDetail,
  placedToDetail,
  settledToDetail,
  droppedToDetail,
  closedToDetail,
  rejectedToDetail,
  leadToDetail,
  type CaseDetail,
} from "@/lib/case-detail";

/** Project a pinned matter's current book card into the case-detail superset,
 *  reusing each book's existing mapper. Intake leads ride `leadToDetail`
 *  (IntakeLead extends Lead). */
export function cardToDetail(card: SurfaceCard): CaseDetail {
  switch (card.kind) {
    case "intake":
      return leadToDetail(card.card);
    case "under-review-lead":
      return underReviewLeadToDetail(card.card);
    case "under-review-misc":
      return underReviewMiscToDetail(card.card);
    case "accepted":
      return acceptedToDetail(card.card);
    case "placed":
      return placedToDetail(card.card);
    case "settled":
      return settledToDetail(card.card);
    case "dropped":
      return droppedToDetail(card.card);
    case "closed":
      return closedToDetail(card.card);
    case "rejected":
      return rejectedToDetail(card.card);
    case "lead":
      return leadToDetail(card.card);
  }
}

/** The stage-relevant "where is it" facts, rendered only when present — a thin
 *  matter yields a thin card, same posture as the detail panel. */
function signalParts(detail: CaseDetail): string[] {
  const parts: string[] = [];
  if (detail.fsa?.status === "outstanding") parts.push("FSA outstanding");
  if (detail.nextFilingDeadline) {
    parts.push(
      `Filing deadline ${fmtDate(detail.nextFilingDeadline, { year: true })}`,
    );
  }
  if (detail.lastMonthlyUpdateDate) {
    parts.push(
      `Last update ${fmtDate(detail.lastMonthlyUpdateDate, { year: true })}`,
    );
  }
  return parts;
}

export function PinnedCard({
  pin,
  openDetail,
  viewerEmail,
}: {
  pin: SurfacePin;
  openDetail: (detail: CaseDetail) => void;
  /** Default assignee for the "+ To-do" modal. */
  viewerEmail: string;
}) {
  const action = useTaskAction();
  const [notesOpen, setNotesOpen] = useState(false);
  const [todoOpen, setTodoOpen] = useState(false);
  const detail = cardToDetail(pin.card);
  const phase = phaseForCardKind(pin.card.kind);

  const facts = [detail.caseType, detail.incidentState]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(" · ");
  const signals = signalParts(detail);
  const leadId =
    pin.task.about?.kind === "lead" ? pin.task.about.id : null;

  return (
    <div
      className="rounded-lg border bg-card text-card-foreground shadow-sm"
      style={
        phase ? { borderLeftWidth: 3, borderLeftColor: phase.accent } : undefined
      }
      data-testid="pinned-row"
    >
      <div className="flex items-start gap-3 px-3 py-2.5">
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => openDetail(detail)}
              className="text-sm font-medium text-foreground hover:text-interactive"
              data-testid="pinned-open-detail"
            >
              {detail.name}
            </button>
            {phase ? (
              <span className="text-[11px] text-muted-foreground">
                {phase.label}
              </span>
            ) : null}
            {detail.status && detail.status !== phase?.label ? (
              <Badge variant="muted">{detail.status}</Badge>
            ) : null}
            {detail.signed ? <Badge variant="blue">Transfer</Badge> : null}
          </div>
          {facts || detail.originatingFirmName || detail.handlingFirmName ? (
            <div className="text-xs text-muted-foreground" data-testid="pinned-facts">
              {[
                facts,
                detail.originatingFirmName
                  ? `OF · ${detail.originatingFirmName}`
                  : null,
                detail.handlingFirmName
                  ? `→ ${detail.handlingFirmName}`
                  : null,
              ]
                .filter(Boolean)
                .join("  ·  ")}
            </div>
          ) : null}
          {signals.length > 0 ? (
            <div
              className="text-xs text-muted-foreground"
              data-testid="pinned-signals"
            >
              {signals.join("  ·  ")}
            </div>
          ) : null}
          {pin.task.body && pin.task.body.trim() !== "" ? (
            <p className="text-[13px] text-foreground/80">{pin.task.body}</p>
          ) : null}
          <div className="text-[11px] text-muted-foreground/70">
            Pinned {fmtDate(pin.task.created, { year: true })}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 self-center">
          <button
            type="button"
            onClick={() => setNotesOpen((o) => !o)}
            aria-expanded={notesOpen}
            data-testid="pinned-notes-toggle"
            className="rounded border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            Notes
          </button>
          {leadId !== null ? (
            <button
              type="button"
              onClick={() => setTodoOpen(true)}
              data-testid="pinned-todo"
              className="rounded border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              + To-do
            </button>
          ) : null}
          <button
            type="button"
            disabled={action.isPending}
            onClick={() => action.mutate({ id: pin.task.id, action: "clear" })}
            data-testid="pinned-unpin"
            className="rounded border px-2 py-0.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
          >
            {action.isPending ? "Un-pinning…" : "Un-pin"}
          </button>
        </div>
      </div>
      {notesOpen ? <PinNotes taskId={pin.task.id} /> : null}
      {todoOpen && leadId !== null ? (
        <CreateTaskModal
          open
          onClose={() => setTodoOpen(false)}
          viewerEmail={viewerEmail}
          initialAbout={{ kind: "lead", id: leadId }}
          initialAboutLabel={detail.name}
        />
      ) : null}
    </div>
  );
}

/** The pin's note thread — the existing task-messages door, inline. */
function PinNotes({ taskId }: { taskId: string }) {
  const messages = useTaskMessages(taskId, true);
  const add = useAddMessage(taskId);
  const [draft, setDraft] = useState("");

  const submit = () => {
    const body = draft.trim();
    if (body === "" || add.isPending) return;
    add.mutate({ body }, { onSuccess: () => setDraft("") });
  };

  const list = messages.data?.messages ?? [];

  return (
    <div className="space-y-2 border-t px-3 py-2" data-testid="pinned-notes">
      {messages.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading notes…</p>
      ) : list.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="pinned-notes-empty">
          No notes yet.
        </p>
      ) : (
        list.map((m) => (
          <div key={m.id} className="text-xs" data-testid="pinned-note">
            <span className="text-muted-foreground">
              {operatorName(m.author)} · {fmtDate(m.timestamp, { year: true })}
            </span>
            <p className="whitespace-pre-wrap text-[13px] text-foreground/90">
              {m.body}
            </p>
          </div>
        ))
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Add a note…"
          className="w-full resize-none rounded border bg-background px-2 py-1 text-xs"
          data-testid="pinned-note-draft"
        />
        <button
          type="button"
          onClick={submit}
          disabled={add.isPending || draft.trim() === ""}
          data-testid="pinned-note-add"
          className="shrink-0 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
        >
          {add.isPending ? "Adding…" : "Add note"}
        </button>
      </div>
      {add.isError ? (
        <p className="text-[11px] text-error">Couldn't save — try again.</p>
      ) : null}
    </div>
  );
}
